/**
 * Idempotency keys for manual emission creation (plan 8.7).
 *
 * The server keeps the response of a create under its key, so a key must satisfy two opposing
 * rules at once: every retry of the *same* save must reuse it, and a genuinely different save must
 * never reuse it. Getting this backwards is worse than having no key at all - a reused key across
 * two different emissions returns the first result and silently discards the second.
 *
 * The unit here is the "submission attempt", not the network call: a double-click sends two
 * requests for one save, and that is precisely the case 8.7 exists to catch. So the key is minted
 * when a save begins and cleared only when the save definitively succeeds or the form is reset.
 * A failed save keeps its key, because retrying the same save is exactly what should replay.
 */

const HEX = "0123456789abcdef";

/**
 * Creates an idempotency key. Uses `crypto.randomUUID` where available and falls back to
 * `getRandomValues`; a plain `Math.random` key would be guessable, and guessable keys are how two
 * submissions collide by accident.
 */
export function createRequestKey(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi?.getRandomValues) {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    let key = "";
    for (const byte of bytes) {
      key += HEX[byte % 16] + HEX[Math.floor(byte / 16)];
    }
    return key;
  }
  // No Web Crypto at all. Still better than a constant, which would collapse every submission
  // in the session onto one key.
  return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Holds the key for the save currently in progress.
 *
 * `current` returns the existing key so retries reuse it, and mints one on first use. `clear`
 * is called on success and on reset so the next distinct save gets a new key.
 */
export class SubmissionKeyHolder {
  private key: string | null = null;

  current(): string {
    if (this.key === null) {
      this.key = createRequestKey();
    }
    return this.key;
  }

  clear(): void {
    this.key = null;
  }
}
